// src/data/coverage/gapInstruments.ts
// ============================================================
// INSTRUMENTS — what closes a gap, and the org-level risk coefficient line
//
// An instrument is something the client can supply that would move a measure onto a
// stronger tier. The gap list names one per measure so the panel answers "what do I do
// about it" rather than only "here is what is missing".
//
// ─────────────────────────────────────────────────────────────────────────────
// KNOWN COVERAGE HOLE, stated rather than hidden.
//
// The instruments below reach 27 of the 38 measures. The other 11 have NO instrument:
//   1, 2, 3, 4  (all of Financial Value; 1 and 2 come from the profile instead)
//   45  (Change Management Capability)
//   47  (Partnerships and Engagement)
//   52, 53, 54, 55, 56  (all of Implementation and Market except 51)
//
// D4 closed most of the previous hole. Compliance and Risk was 5 of 8 unreachable and is
// now fully covered, including 37, 38 and 40, which sat on library default with nothing
// able to inform them at all. Closing the rest means new instruments, which is a product
// decision rather than a rendering one.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

export type InstrumentId = 'D1' | 'D2' | 'D3' | 'D4' | 'pathA';

export interface Instrument {
  id: InstrumentId;
  /** Display name. Rendered verbatim. */
  label: string;
  /** One short sentence on what the client supplies. Rendered verbatim. */
  description: string;
  measureIds: number[];
  /** Card copy. Absent on Path A, which has no card of its own. */
  card?: InstrumentCard;
  /** Document Upload page presentation. Absent on Path A. */
  display?: InstrumentDisplay;
}

/**
 * The upload-card copy.
 *
 * MEASURE COUNTS ARE NOT IN THESE STRINGS. `whatItChanges` names the areas; the count is
 * rendered from measureIds.length by the card. A typed count is a number a component holds
 * that the data can derive, which is the one thing this layer does not allow.
 *
 * D1 CARRIES NO RE-RANKING CLAIM, deliberately. Its effect routes largely through the
 * organizational risk coefficient, which is org-level: it moves score level and band
 * placement across every opportunity equally, so it does not reorder a list. Promising a
 * reordered list from an org-level input would be a false claim, and a test asserts the
 * words rank, reorder and re-rank appear nowhere in D1's copy.
 */
export interface InstrumentCard {
  whatItIs: string;
  whatItChanges: string;
  effort: string;
}

/**
 * How an instrument presents on the Document Upload page.
 *
 * `effect` is a CLAIM about what supplying the file does, so each one is held to what the
 * engine actually produces. D2 and D3 move measures that carry index weight and can say
 * they re-rank. D1 MAY NOT: its measures move score level and band placement across every
 * opportunity rather than reordering a list, and a test asserts its copy carries no rank,
 * re-rank or reorder language.
 *
 * `flag` orders attention rather than effort. D2 is the lightest ask so it is where to
 * start; D3 moves the most. D1 carries no flag because it is neither.
 */
export interface InstrumentDisplay {
  /** Short code shown in the chip. */
  code: string;
  flag?: string;
  /** One line on what supplying it does. See the note above on D1. */
  effect: string;
  /** Chip copy: the ask, at a glance. */
  effortChip: string;
  /** The detail beside the links. */
  effortDetail: string;
}

export const INSTRUMENTS: readonly Instrument[] = [
  {
    id: 'D3',
    label: 'Data Asset and Platform Inventory',
    description: 'A list of your data assets with their platform, standard, availability and refresh cadence.',
    measureIds: [26, 27, 28, 29, 30, 31, 32],
    card: {
      whatItIs:
        'One row per platform or data domain you hold. Depth, refresh cadence, standards, and your own quality rating.',
      whatItChanges:
        'Measures covering data quality, standardization, accessibility, cleansing and preparation, interoperability, historical depth, and real-time capability. This is the largest block inside implementation feasibility, so it re-ranks data-intensive opportunities directly.',
      effort: 'Thirty minutes if you know your stack. Nine rows is typical.',
    },
    display: {
      code: 'D3',
      flag: 'Moves the most',
      effect: 'Re-ranks data-intensive opportunities directly.',
      effortChip: '30 min',
      effortDetail: 'Nine rows typical',
    },
  },
  {
    id: 'D1',
    label: 'Staffing and Capacity Inventory',
    description: 'Your teams, their skill areas, and how much of each team is already committed.',
    measureIds: [42, 43, 46, 51],
    card: {
      whatItIs:
        'One row per team in your data, analytics, and digital function. Headcount, how much of it is already committed, and what skills you have.',
      // NO RE-RANKING CLAIM. See the InstrumentCard note above.
      whatItChanges:
        'Measures covering staff expertise, resource availability, infrastructure support, and implementation complexity. It lets the system compare what an opportunity requires against what you actually have free, rather than against an industry assumption.',
      effort: 'Fifteen minutes. Three to five rows is typical.',
    },
    display: {
      code: 'D1',
      // NO RE-RANKING CLAIM. See the note on InstrumentDisplay.
      effect: 'Moves score level and band placement across every opportunity, not the order of the list.',
      effortChip: '15 min',
      effortDetail: 'Three to five rows typical',
    },
  },
  {
    id: 'D2',
    label: 'Board Priorities Extract',
    description: 'Your board priorities, each on a theme, with whether it is funded.',
    measureIds: [5, 6, 7, 8, 21, 22, 24],
    card: {
      whatItIs:
        'Your current fiscal year priorities. Upload the brief you already have, or fill in the short template if you would rather give us structure.',
      whatItChanges:
        'All of the strategic alignment measures. It also tells the system which priorities you have not set, which is often the more useful half.',
      effort: 'None if you upload what exists.',
    },
    display: {
      code: 'D2',
      flag: 'Start here',
      effect: 'Re-ranks opportunities across every strategic alignment measure.',
      effortChip: 'No prep',
      effortDetail: 'Upload the brief that exists',
    },
  },
  {
    id: 'D4',
    label: 'Governance and Compliance Charter',
    description: 'Your governance and control areas, each with what exists today and how mature it is.',
    // NINE MEASURES, not the five the source material named (34, 35, 38, 41, 44). The
    // charter also asks about legal and contractual agreements, third party review,
    // multi-jurisdiction operations and regulatory framework alignment, so it reaches 36,
    // 37, 39 and 40 as well. Three of those (37, 38, 40) are on library default with no
    // other instrument able to inform them, which is what makes this the biggest gain.
    measureIds: [34, 35, 36, 37, 38, 39, 40, 41, 44],
    card: {
      whatItIs:
        'One row per governance or control area. What exists today, who owns it, and how mature it is on a 1 to 5 scale.',
      whatItChanges:
        'Measures covering security and privacy, de-identification, regulatory alignment, legal clarity, auditability, vendor review, jurisdiction, consent, and how analytics work is governed. Compliance burden is real friction on delivery, so it re-ranks opportunities carrying regulatory or consent exposure.',
      effort: 'Forty-five minutes. Nine control areas is typical.',
    },
    display: {
      code: 'D4',
      flag: 'Biggest gap',
      effect: 'Re-ranks opportunities carrying regulatory or consent exposure.',
      effortChip: '45 min',
      effortDetail: 'Nine control areas typical',
    },
  },
  {
    id: 'pathA',
    label: 'Board and strategy documents',
    description: 'Your board plan, annual report or priorities brief, uploaded as a document.',
    measureIds: [5, 6, 7, 8, 21, 22, 24],
  },
] as const;

/**
 * The instrument that closes a given measure, or null.
 *
 * Where more than one reaches a measure the STRUCTURED instrument wins, because a workbook
 * produces a score from the cells while a document only asserts a binding.
 */
export function instrumentForMeasure(measureId: number): Instrument | null {
  return INSTRUMENTS.find((i) => i.measureIds.includes(measureId)) ?? null;
}

/** Measures no instrument reaches. Derived, so it cannot fall out of step with the list. */
export function hasInstrument(measureId: number): boolean {
  return instrumentForMeasure(measureId) !== null;
}

// ---- organizational risk coefficient ---------------------------------------

/**
 * ORG-LEVEL, SPECIFIED BUT NOT IMPLEMENTED.
 *
 * The coefficient is defined and sits at its neutral value. Nothing consumes it, and no
 * ranking or score anywhere in the app reads it. The copy below has to say that without
 * either overclaiming (implying it is re-ranking something) or reading as broken.
 *
 * Keep NEUTRAL_VALUE next to the copy so the panel cannot render a different number from
 * the one this module calls neutral.
 */
export const ORG_RISK_COEFFICIENT = {
  label: 'Organizational risk coefficient',
  neutralValue: 1.0,
  /**
   * What it is doing right now. Names the VRI explicitly: the composite score IS the VRI,
   * and a reader needs to know it is the thing not being adjusted.
   */
  status: 'Running at its neutral default. It is not adjusting the VRI or any ranking.',
  /** What would move it. */
  movedBy: ['Staffing and Capacity Inventory', 'Board Priorities Extract'],
} as const;
